import {
  List,
  Button,
  Tooltip,
  Loading,
  Avatar,
} from '@trufla-technology/trumarket-react-design-system';
import { Link, useHistory } from 'react-router-dom';
import { useListUsers } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { FilterForList } from '../../../components/ListFilter';
import { timeAgo } from 'app/utils/date';
import { useReadableDateTime } from 'app/hooks/useReadableDateTime';
import './style.scss';
import { nameInitials } from 'app/utils';
import { useAuthContext } from '@trufla-technology/trufla-trudomain/dist/lib';
import { useEffect, useState } from 'react';
import { ListUsersResponse } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { debounce } from 'lodash';

export const UsersList = () => {
  const { isPermitted } = useAuthContext();
  const history = useHistory();
  const readableDateFormat = useReadableDateTime();
  const [users, setUsers] = useState<NonNullable<ListUsersResponse['data']>>(
    [],
  );

  const {
    data,
    paging,
    sortIndex,
    handleSort,
    handleOffset,
    handleFilter,
    query,
    loading,
  } = useListUsers({
    paginate: { offset: 0, limit: 25 },
    sort: [
      {
        sortBy: 'fullName',
        sortOrder: 'asc',
      },
    ],
  });
  useEffect(() => {
    if (!data || data.length < 1) return;
    setUsers([...users, ...data]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const loadMore = () => {
    handleOffset(query.paginate?.limit! + query.paginate?.offset!);
  };

  const handleSearch = debounce((value: string): void => {
    const searchWord = value?.toLowerCase();
    setUsers([]);

    if (!searchWord) {
      handleFilter({}, { override: true });
      return;
    }

    handleFilter({ fullName: searchWord });
  }, 500);

  if (!data)
    return (
      <div className="loading-wrapper">
        <Loading />
      </div>
    );

  return (
    <div className="component-wrapper user-list">
      <div className="component-wrapper--header">
        <p>Users ({paging?.count})</p>
        {isPermitted('createUser') && (
          <Button
            type="primary"
            onClick={() => history.push({ search: '?new=user' })}
          >
            Add User
          </Button>
        )}
      </div>
      <FilterForList placeholder="Search for Name" onSearch={handleSearch} />
      {!loading && data.length === 0 && (
        <article className="empty-list"> No Users Found</article>
      )}

      {data.length !== 0 ? (
        <List
          loadMore={loadMore}
          hasMore={(paging?.count ?? 0) > (users?.length ?? 0)}
          data={users || []}
          columns={[
            {
              field: 'fullName',
              label: 'User Name',
              render: ({ key, fullName }) => (
                <Link to={`/users/${key}`}>
                  <div className="client-list-avatar">
                    <Avatar>{nameInitials(fullName)}</Avatar>
                    <span>{fullName}</span>
                  </div>
                </Link>
              ),
              sortOrder: sortIndex.fullName,
              onSort: () => handleSort('fullName'),
            },
            {
              label: 'Email',
              field: 'email',
              render: ({ email }) => <span>{email}</span>,
              sortOrder: sortIndex['email'],
              onSort: () => handleSort('email'),
            },
            {
              label: 'Phone Number',
              field: 'phoneNumber',
              render: ({ phoneNumber }) => <span>{phoneNumber}</span>,
              sortOrder: sortIndex['phoneNumber'],
              onSort: () => handleSort('phoneNumber'),
            },
            {
              label: 'Status',
              field: 'active',
              render: ({ active }) =>
                active ? <span>Active</span> : <span>Inactive</span>,
              sortOrder: sortIndex['active'],
              onSort: () => handleSort('active'),
            },
            {
              label: 'Job Title',
              field: 'jobTitle',
              render: ({ jobTitle }) => <span>{jobTitle}</span>,
              sortOrder: sortIndex['jobTitle'],
              onSort: () => handleSort('jobTitle'),
            },
            {
              label: 'Date Created',
              field: 'createdAt',

              render: ({ createdAt }) => (
                <Tooltip position="right bottom" title={timeAgo(createdAt)}>
                  <span style={{ cursor: 'default', paddingRight: '0.2rem' }}>
                    {readableDateFormat(createdAt!, 'dd/LL/yyyy, h:mma')}
                  </span>
                </Tooltip>
              ),
              sortOrder: sortIndex['createdAt'],
              onSort: () => handleSort('createdAt'),
            },
          ]}
          loading={loading}
        />
      ) : null}
    </div>
  );
};
